import type { AttributeCatalogResponse } from '@shared/api';
import type { CustomAttribute } from '@shared/config';
import type { DiscoverySourceId } from '@shared/sources';
import { useQuery } from '@tanstack/react-query';
import { Info, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Alert, Badge, Spinner } from '../../components/ui/feedback';
import { Checkbox, Input } from '../../components/ui/form';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/query';
import { CustomAttributes } from './custom-attributes';

export interface StepAttributesProps {
  sources: DiscoverySourceId[];
  websiteEnrichment: boolean;
  selected: string[];
  custom: CustomAttribute[];
  onSelectedChange: (selected: string[]) => void;
  onCustomChange: (custom: CustomAttribute[]) => void;
  errors: Record<string, string>;
}

/** Step 3. Pick catalog attributes, then add any number of custom natural-language ones. */
export function StepAttributes({
  sources,
  websiteEnrichment,
  selected,
  custom,
  onSelectedChange,
  onCustomChange,
  errors,
}: StepAttributesProps) {
  const [filter, setFilter] = useState('');

  const catalog = useQuery({
    queryKey: queryKeys.catalogAttributes(sources, websiteEnrichment),
    queryFn: () =>
      api.get<AttributeCatalogResponse>(
        `/api/catalog/attributes?sources=${encodeURIComponent(sources.join(','))}&enrichment=${String(websiteEnrichment)}`,
      ),
  });

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const needle = filter.trim().toLowerCase();

  const groups = useMemo(() => {
    const all = catalog.data?.groups ?? [];
    if (!needle) return all;
    return all
      .map((group) => ({
        ...group,
        attributes: group.attributes.filter(
          (attribute) =>
            attribute.label.toLowerCase().includes(needle) ||
            attribute.key.toLowerCase().includes(needle) ||
            (attribute.description ?? '').toLowerCase().includes(needle),
        ),
      }))
      .filter((group) => group.attributes.length > 0);
  }, [catalog.data, needle]);

  const visibleKeys = useMemo(() => groups.flatMap((group) => group.attributes.map((a) => a.key)), [groups]);

  const setKeys = (keys: string[], on: boolean): void => {
    const next = new Set(selectedSet);
    for (const key of keys) {
      if (on) next.add(key);
      else next.delete(key);
    }
    onSelectedChange([...next]);
  };

  if (catalog.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner /> Loading the attribute catalog…
      </div>
    );
  }

  const catalogSelectedCount = selected.filter((key) => !key.startsWith('custom.')).length;

  return (
    <div className="space-y-5">
      {errors['selected'] ? <Alert tone="error">{errors['selected']}</Alert> : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            className="pl-8"
            value={filter}
            placeholder="Search attributes"
            aria-label="Search attributes"
            onChange={(event) => setFilter(event.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="tabular text-[13px] text-muted-foreground">{catalogSelectedCount} selected</span>
          <Button type="button" size="sm" variant="outline" onClick={() => setKeys(visibleKeys, true)}>
            Select all
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setKeys(visibleKeys, false)}>
            Clear
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onSelectedChange([...(catalog.data?.defaults ?? []), ...selected.filter((k) => k.startsWith('custom.'))])}
          >
            Reset to defaults
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {groups.map((group) => {
          const keys = group.attributes.map((attribute) => attribute.key);
          const allOn = keys.every((key) => selectedSet.has(key));

          return (
            <section key={group.id} className="rounded-[var(--radius-panel)] border border-border bg-surface">
              <header className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
                <div className="min-w-0">
                  <h3 className="text-[13px] font-semibold text-foreground">{group.label}</h3>
                  <p className="text-[13px] text-muted-foreground">{group.description}</p>
                </div>
                <Button type="button" size="sm" variant="ghost" onClick={() => setKeys(keys, !allOn)}>
                  {allOn ? 'None' : 'All'}
                </Button>
              </header>
              <ul className="grid gap-x-4 gap-y-1.5 p-3 sm:grid-cols-2 lg:grid-cols-3">
                {group.attributes.map((attribute) => (
                  <li key={attribute.key} className="flex items-start gap-2">
                    <Checkbox
                      checked={selectedSet.has(attribute.key)}
                      onChange={(event) => setKeys([attribute.key], event.target.checked)}
                      aria-label={attribute.label}
                    />
                    <span className="min-w-0">
                      <span className="block text-[13px] text-foreground">{attribute.label}</span>
                      {attribute.costNote ? (
                        <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-warning">
                          <Info className="h-3 w-3" aria-hidden="true" /> {attribute.costNote}
                        </span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
        {groups.length === 0 ? <p className="text-[13px] text-muted-foreground">No attributes match “{filter}”.</p> : null}
      </div>

      <section>
        <header className="mb-2 flex items-center gap-2">
          <h3 className="text-[13px] font-semibold text-foreground">Custom attributes</h3>
          <Badge tone="accent">{custom.length}</Badge>
        </header>
        <p className="mb-2 text-[13px] text-muted-foreground">
          Anything the catalog does not cover — described in your own words, answered per business.
        </p>
        <CustomAttributes attributes={custom} onChange={onCustomChange} error={errors['custom']} />
      </section>
    </div>
  );
}
